import { posApi } from "../../api/client";
import {
  listLocalOrdersForShift,
  listUnsyncedLocalCustomerDrafts,
  markLocalCustomerDraftSyncStatus,
  updateLocalOrderCustomerSnapshot,
} from "./db";

export type DrainLocalCustomerDraftsInput = {
  branchId: string;
  shiftId?: string;
  onProgress?: (progress: DrainLocalCustomerDraftsProgress) => void;
};

export type DrainLocalCustomerDraftsProgress = {
  total: number;
  synced: number;
  failed: number;
  pending: number;
  message: string;
};

export type DrainLocalCustomerDraftsResult = {
  synced: number;
  failed: number;
  skipped: number;
  total: number;
};

async function updateShiftOrdersForSyncedDraft(input: {
  shiftId?: string;
  localCustomerId: string;
  serverCustomerId: string;
  phone: string;
  firstName?: string;
  lastName?: string;
}) {
  if (!input.shiftId) return;

  const orders = await listLocalOrdersForShift(input.shiftId);
  const matchingOrders = orders.filter(
    (order) =>
      order.customerSnapshot?.localCustomerId === input.localCustomerId,
  );

  for (const order of matchingOrders) {
    await updateLocalOrderCustomerSnapshot(order.clientOrderId, {
      customerId: input.serverCustomerId,
      customerSyncStatus: "synced",
      customerSnapshot: order.customerSnapshot
        ? {
            ...order.customerSnapshot,
            customerId: input.serverCustomerId,
            phone: order.customerSnapshot.phone ?? input.phone,
            firstName: order.customerSnapshot.firstName ?? input.firstName,
            lastName: order.customerSnapshot.lastName ?? input.lastName,
            source: "server",
          }
        : undefined,
    });
  }
}

export async function drainLocalCustomerDraftsForBranch({
  branchId,
  shiftId,
  onProgress,
}: DrainLocalCustomerDraftsInput): Promise<DrainLocalCustomerDraftsResult> {
  const drafts = await listUnsyncedLocalCustomerDrafts(branchId);
  if (drafts.length === 0) {
    return { synced: 0, failed: 0, skipped: 0, total: 0 };
  }

  let synced = 0;
  let failed = 0;

  onProgress?.({
    total: drafts.length,
    synced,
    failed,
    pending: drafts.length,
    message: `ພົບ ${drafts.length} customer draft ກຳລັງ sync...`,
  });

  for (const draft of drafts) {
    await markLocalCustomerDraftSyncStatus(draft.localCustomerId, {
      syncStatus: "syncing",
      lastSyncError: undefined,
    });

    try {
      const customer = await posApi.findOrCreateCustomerByPhone(draft.phone);
      await markLocalCustomerDraftSyncStatus(draft.localCustomerId, {
        syncStatus: "synced",
        serverCustomerId: customer.id,
        lastSyncError: undefined,
        syncedAt: new Date().toISOString(),
      });
      await updateShiftOrdersForSyncedDraft({
        shiftId,
        localCustomerId: draft.localCustomerId,
        serverCustomerId: customer.id,
        phone: customer.phone ?? draft.phone,
        firstName: customer.firstName ?? draft.firstName,
        lastName: customer.lastName ?? draft.lastName,
      });
      synced += 1;
    } catch (error) {
      failed += 1;
      await markLocalCustomerDraftSyncStatus(draft.localCustomerId, {
        syncStatus: "failed",
        lastSyncError:
          error instanceof Error
            ? error.message
            : "Failed to sync customer draft",
        retryCount: (draft.retryCount ?? 0) + 1,
      });
    }

    onProgress?.({
      total: drafts.length,
      synced,
      failed,
      pending: drafts.length - synced - failed,
      message: `sync customer draft ສຳເລັດ ${synced}, ລົ້ມເຫຼວ ${failed}`,
    });
  }

  return { synced, failed, skipped: 0, total: drafts.length };
}
